/**
 * Pure post-auth redirect decision logic for AuthGuard.
 *
 * NOTE: This file is the documented exception to the project's "default to no comments"
 * policy. The user flows below are non-obvious from code, and a prod incident
 * (a single misplaced `if (savedRedirect) return savedRedirect`) caused an infinite
 * redirect loop and blank screen on login. Comments here are load-bearing —
 * read them before changing this file.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * CANONICAL USER FLOWS (all must be preserved by any change here)
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Flow A — Deep link to a workspace resource (e.g. /workspaces/1a2mB/scenario/By0nD):
 *   A1. NEW user (invited): open link → login → ToS → invite acceptance → land on the link.
 *   A2. EXISTING logged-out user (invited): open link → login → invite acceptance → land on the link.
 *   A3. EXISTING logged-in user (invited): open link → invite acceptance → land on the link.
 *   A4. EXISTING member, logged out: open link → login → land on the link directly.
 *
 * Flow B — Root URL (/):
 *   B1. NEW user: open / → login → ToS → auto-create default workspace → land in its library.
 *   B2. EXISTING user: open / → land in `latestVisitedOrganization` library
 *       (or first available org if no latest-visited).
 *   B3. NoPressure B2B person with NO original workspace (`has_b2b_workspace_or_invitation`
 *       on /v1/users/me): open / → land on the B2B home `/work`; no workspace is created.
 *       Checked after B2, so anyone with an original workspace keeps B2; without the flag
 *       a new user still gets B1.
 *
 * Flow C — Clone-workspace link (/clone-workspace/:templateId):
 *   Anyone opens it → login (if needed) → ToS (if needed) → land back on the link.
 *   The page performs the clone on mount; each click of a clone link by an
 *   existing member produces a new clone.
 *
 * Flow D — 401 mid-session:
 *   Background API call returns 401 → custom-fetch saves current path and
 *   redirects to login → after re-login, land back on the saved path.
 *
 * Flow E — NoPressure B2B portal page (/work or /work/…, e.g. the practice link a learner's agent gave):
 *   A learner signed out, or owing ToS or a first name, opens it → login / ToS /
 *   name onboarding → land back on the link. A B2B learner has no workspace in the
 *   path, so without this flow the link would be 'unrelated' and the fallback would
 *   create a default workspace for them.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * THE PROD INCIDENT (regression guard)
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Symptom: blank screen after login. Network tab showed only one
 * GET /v1/organizations/ with no POST follow-up.
 *
 * Cause: when the 401 handler on the root URL had saved
 * `nopressure:redirectAfterLogin = '/'`, the resolver matched a catch-all
 * `if (savedRedirect) return savedRedirect`, returning '/'. Navigating to '/'
 * re-entered the same public AuthGuard. useEffect deps did not change
 * (same mode/user/tosAccepted), so the resolver did not re-run. State stayed
 * at '/' forever → infinite <Navigate /> loop.
 *
 * Structural fix in this file: a saved redirect is *classified* into a
 * discriminated union (`SavedRedirectClass`). `'unrelated'` (path with no
 * workspace ID and not /clone-workspace/, /invite or the B2B portal /work) explicitly falls through to the
 * latest-visited / first-org / B2B-home / create-default chain. The compiler enforces
 * exhaustive handling of every kind in the resolver and in AuthGuard's switch.
 * The B2B-home destination carries no path, so it can never echo the saved one.
 */
import type { OrganizationsForUserResponse } from '@np-forge/api-client/types'

import { isB2bPortalPath, ROUTES } from '@/routes/paths'
import { extractOrgIdFromPath } from '@/services/redirectState.service'

/**
 * What kind of saved redirect (if any) is in localStorage, evaluated against
 * the current user's org membership.
 */
export type SavedRedirectClass =
  | { kind: 'none' }
  | { kind: 'workspace-member'; path: string; organizationId: string }
  | { kind: 'workspace-invitee'; path: string; organizationId: string }
  | { kind: 'workspace-no-access'; path: string; organizationId: string }
  | { kind: 'clone-workspace'; path: string }
  | { kind: 'invite-link'; path: string }
  | { kind: 'b2b-page'; path: string }
  | { kind: 'unrelated'; path: string }

/**
 * The decision for where to send a freshly-authenticated, ToS-accepted user
 * arriving on a public route.
 *
 * Each variant is the destination + the implicit side-effect contract:
 *   - `go-to-saved`        : navigate; saved redirect is cleared by protected
 *                            guard on successful render.
 *   - `accept-invitation`  : navigate to invite page; saved redirect is KEPT
 *                            so OrganizationInvite can return there on accept.
 *   - `clone-workspace`    : clear saved (we're about to use it), then navigate.
 *   - `go-to-saved-no-access`: navigate; protected guard will render the
 *                            no-access screen. Saved is cleared (dead-end).
 *   - `workspace-library`  : computed library URL; navigate.
 *   - `b2b-home`           : the B2B portal's home; navigate. Saved redirect is cleared by
 *                            the protected guard when /work renders.
 *   - `create-default-workspace`: bootstrap a new org, then navigate to its library.
 *   - `tos`                : fallback when listOrganizations() failed.
 */
export type PostAuthDestination =
  | { kind: 'go-to-saved'; path: string } // flow: A4 (existing member, deep link)
  | { kind: 'accept-invitation'; organizationId: string } // flow: A1, A2, A3
  | { kind: 'clone-workspace'; path: string } // flow: C
  | { kind: 'invite-link'; path: string } // flow: invite-link — returning logged-out user redeems on /invite
  | { kind: 'b2b-page'; path: string } // flow: E — back to the B2B page, no workspace involved
  | { kind: 'go-to-saved-no-access'; path: string } // saved org user is not member/invited of
  | { kind: 'workspace-library'; organizationId: string; reason: 'latest-visited' | 'first-org' } // flow: B2
  | { kind: 'b2b-home' } // flow: B3 — no path on purpose: it can never echo the saved one
  | { kind: 'create-default-workspace' } // flow: B1
  | { kind: 'tos' } // listOrganizations() failed
  | { kind: 'bootstrap-failed'; error: Error } // executeDestination threw — render error UI with reload

/**
 * Classify a saved redirect against the user's org membership. Pure: no I/O,
 * no effects.
 *
 * Order of checks matters for clone-workspace links: a path could in theory be
 * `/clone-workspace/` with a workspace-shaped suffix; we treat /clone-workspace/
 * as the dominant signal (it's the user's intended action).
 */
export function classifySavedRedirect(
  saved: string | null,
  workspaces: OrganizationsForUserResponse,
): SavedRedirectClass {
  if (!saved) return { kind: 'none' }

  if (saved.startsWith('/clone-workspace/')) {
    return { kind: 'clone-workspace', path: saved }
  }

  // Invite-link return path: a returning logged-out user who started on /invite
  // saved a TOKEN-FREE /invite(?to=…) redirect (the token lives in sessionStorage).
  // It carries no workspace id, so without this branch it would classify as
  // 'unrelated' and fall through to the library — stranding the redeem. Like
  // clone-workspace, /invite is a non-workspace action route the user is returned
  // to so the page can act on mount (here: redeem the stashed token).
  if (saved === ROUTES.INVITE.fullPath || saved.startsWith(`${ROUTES.INVITE.fullPath}?`)) {
    return { kind: 'invite-link', path: saved }
  }

  // Flow E: every NoPressure B2B page lives in the B2B portal (/work) and carries no workspace id. It is the
  // user's intended destination, like a clone link, so it never reaches the fallback chain.
  if (isB2bPortalPath(saved)) {
    return { kind: 'b2b-page', path: saved }
  }

  const organizationId = extractOrgIdFromPath(saved)
  if (!organizationId) return { kind: 'unrelated', path: saved }

  const isMember = workspaces.organizations.some(o => o.id === organizationId)
  if (isMember) return { kind: 'workspace-member', path: saved, organizationId }

  const isInvited = workspaces.invitations.some(o => o.id === organizationId)
  if (isInvited) return { kind: 'workspace-invitee', path: saved, organizationId }

  return { kind: 'workspace-no-access', path: saved, organizationId }
}

/**
 * Given a saved-redirect classification and the user's available orgs, decide
 * where to send the user. Pure: no I/O, no effects.
 *
 * The exhaustive switch is the critical bit — adding a new `SavedRedirectClass`
 * variant becomes a compile error here, forcing the author to consider how it
 * affects the post-auth flow. The prod-incident catch-all `if (savedRedirect)
 * return savedRedirect` cannot be expressed in this shape.
 */
export function resolvePostAuthDestination(
  classification: SavedRedirectClass,
  latestVisitedOrgId: string | null,
  workspaces: OrganizationsForUserResponse,
  // `has_b2b_workspace_or_invitation` from /v1/users/me. Required, so every caller has to state it.
  hasB2bWorkspaceOrInvitation: boolean,
): PostAuthDestination {
  switch (classification.kind) {
    case 'workspace-member':
      // Flow A4: member opens deep link → land directly on it.
      return { kind: 'go-to-saved', path: classification.path }

    case 'workspace-invitee':
      // Flows A1/A2/A3: route through invite acceptance. Saved redirect is
      // kept so OrganizationInvite returns the user to the original deep link.
      return { kind: 'accept-invitation', organizationId: classification.organizationId }

    case 'clone-workspace':
      // Flow C: clone link is the user's intended destination.
      return { kind: 'clone-workspace', path: classification.path }

    case 'invite-link':
      // Invite-link flow: route the freshly-authenticated user back to /invite so
      // the page redeems the token still parked in sessionStorage. Mirrors
      // clone-workspace (act-on-mount route), but the secret rides sessionStorage,
      // not the saved path.
      return { kind: 'invite-link', path: classification.path }

    case 'b2b-page':
      // Flow E: back to the B2B page; it needs no workspace, so none is created.
      return { kind: 'b2b-page', path: classification.path }

    case 'workspace-no-access':
      // Saved URL points to an org the user neither belongs to nor was invited to.
      // We still navigate to it so the URL bar reflects intent; the protected
      // guard will render the no-access screen.
      return { kind: 'go-to-saved-no-access', path: classification.path }

    case 'none':
    case 'unrelated':
      // No useful saved redirect (e.g. saved="/"). Fall back to latest-visited,
      // first available org, the B2B home, or bootstrap a default workspace.
      // REGRESSION GUARD: 'unrelated' MUST NOT return classification.path —
      // that was the prod bug. The compiler enforces this by not allowing
      // a `path` field on the returned variants below.
      return resolveFallbackDestination(latestVisitedOrgId, workspaces, hasB2bWorkspaceOrInvitation)
  }
}

function resolveFallbackDestination(
  latestVisitedOrgId: string | null,
  workspaces: OrganizationsForUserResponse,
  hasB2bWorkspaceOrInvitation: boolean,
): PostAuthDestination {
  const userOrgIds = workspaces.organizations.map(o => o.id)

  if (latestVisitedOrgId && userOrgIds.includes(latestVisitedOrgId)) {
    return { kind: 'workspace-library', organizationId: latestVisitedOrgId, reason: 'latest-visited' }
  }

  const firstOrgId = userOrgIds[0]
  if (firstOrgId) {
    return { kind: 'workspace-library', organizationId: firstOrgId, reason: 'first-org' }
  }

  // Flow B3: no original workspace, but in the B2B portal → its home, and no workspace is created.
  if (hasB2bWorkspaceOrInvitation) {
    return { kind: 'b2b-home' }
  }

  // Flow B1: brand-new user, no orgs at all → bootstrap one.
  return { kind: 'create-default-workspace' }
}

/**
 * Decision for a user landing on a protected route. Pure shape; the resolver
 * itself does I/O (listOrganizations).
 *
 * On `'allow'`, `organizationId` is set ⇔ the user is a member of an org named
 * in the path. It is absent when the path has no `/workspaces/:id/...` segment
 * (e.g. `/clone-workspace/X`). Callers that need the org id (e.g. to update
 * latest-visited) can read it directly off the decision instead of re-running
 * extractOrgIdFromPath + a membership check.
 */
export type ProtectedAccessDecision =
  | { kind: 'allow'; organizationId?: string }
  | { kind: 'invite-required'; organizationId: string } // user is invited, must accept first
  | { kind: 'no-access' } // org in path but user is neither member nor invitee
  | { kind: 'unknown-error'; error: Error } // listOrganizations failed; fail closed

export function decideProtectedAccess(
  currentPath: string,
  workspaces: OrganizationsForUserResponse,
): ProtectedAccessDecision {
  const organizationId = extractOrgIdFromPath(currentPath)
  if (!organizationId) {
    // Path has no /workspaces/:id/... segment (e.g. /clone-workspace/X).
    // No org-membership check applies.
    return { kind: 'allow' }
  }

  // Membership-first, mirroring classifySavedRedirect. If the backend has a stale
  // invitation row for an org the user already joined, membership wins — otherwise
  // the protected guard would re-bounce a real member to the invite page.
  if (workspaces.organizations?.some(o => o.id === organizationId)) {
    return { kind: 'allow', organizationId }
  }
  if (workspaces.invitations?.some(o => o.id === organizationId)) {
    return { kind: 'invite-required', organizationId }
  }
  return { kind: 'no-access' }
}
